import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  acknowledgeResourceLibraryCategoryTab,
  acknowledgeResourceLibraryTargetHighlight,
  type ResourceLibraryHighlightTab,
} from '@/lib/collaboration/resource-library-highlight-dismiss';
import { acknowledgeStoryboardTargetHighlight } from '@/lib/collaboration/storyboard-highlight-dismiss';
import {
  acknowledgeSoundEffectTargetHighlight,
  acknowledgeStoryboardSoundTrackHighlight,
  getSoundEffectHighlightScope,
  getStoryboardSoundTrackHighlightScope,
} from '@/lib/collaboration/sound-effect-highlight-dismiss';
import {
  acknowledgeStoryboardVideoTargetHighlight,
  acknowledgeStoryboardVideoTrackHighlight,
  acknowledgeVoiceoverTargetHighlight,
  acknowledgeStoryboardVoiceTrackHighlight,
  getStoryboardVideoHighlightScope,
  getStoryboardVideoTrackHighlightScope,
  getStoryboardVoiceTrackHighlightScope,
  getVoiceoverHighlightScope,
} from '@/lib/collaboration/media-highlight-dismiss';
import {
  useCollaborationHighlightStore,
  type CollaborationHighlightContext,
  type CollaborationHighlightTargetType,
} from '@/stores/collaborationHighlightStore';
import { useProject } from '@/hooks/api';
import { useAppStore } from '@/stores/appStore';
import { resolveCanvasRouteProjectId } from '@/lib/collaboration/highlight-route-compat';

import { isRealtimeCollaborationProject } from '@/lib/realtime-collaboration-project';

export function resolveRouteHighlightContext(
  pathname: string
): CollaborationHighlightContext | null {
  if (pathname === '/project/parse-flow') return 'parse_flow';
  if (/^\/project\/[^/]+\/canvas\/?$/u.test(pathname)) return 'canvas';
  if (/^\/project\/[^/]+$/u.test(pathname)) return 'project_page';
  return null;
}

export function resolveRouteProjectId(
  pathname: string,
  search: string,
  currentProjectId: string | null
): string | null {
  if (pathname === '/project/parse-flow') {
    return new URLSearchParams(search).get('parentProjectId') ?? currentProjectId;
  }
  return resolveCanvasRouteProjectId(pathname, currentProjectId);
}

function buildRouteDismissedScope(
  routeProjectId: string | null,
  routeContext: CollaborationHighlightContext | null,
  scope: string | undefined
): string | undefined {
  if (!routeProjectId || !routeContext || !scope) return undefined;
  return `${routeContext}:${routeProjectId}:${scope}`;
}

function useRouteCollaborationHighlightContext() {
  const location = useLocation();
  const currentProjectId = useAppStore((state) => state.currentProjectId);
  const routeContext = resolveRouteHighlightContext(location.pathname);
  const routeProjectId = resolveRouteProjectId(
    location.pathname,
    location.search,
    currentProjectId
  );
  const { data: currentProject } = useProject(routeProjectId);
  const parentProjectId = currentProject?.parentProjectId ?? null;
  const realtimeCollaborationEnabled = isRealtimeCollaborationProject(currentProject);

  return {
    routeContext,
    routeProjectId,
    parentProjectId,
    realtimeCollaborationEnabled,
  };
}

export function useHasCurrentRouteCollaborationHighlight(params: {
  targetType: CollaborationHighlightTargetType;
  targetId: string | null | undefined;
  dismissedScope?: string;
}): boolean {
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();
  const routeDismissedScope = buildRouteDismissedScope(
    routeProjectId,
    routeContext,
    params.dismissedScope
  );

  return useCollaborationHighlightStore((state) => {
    if (!realtimeCollaborationEnabled || !params.targetId || !routeContext || !routeProjectId)
      return false;
    return state.hasHighlight({
      projectId: routeProjectId,
      parentProjectId,
      context: routeContext,
      targetType: params.targetType,
      targetId: params.targetId,
      dismissedScope: routeDismissedScope,
    });
  });
}

export function useHasCurrentRouteCollaborationHighlightForTypes(
  targetTypes: readonly CollaborationHighlightTargetType[],
  elementTypes?: readonly string[],
  dismissedScope?: string
): boolean {
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();
  const stableTargetTypes = useMemo(() => targetTypes, [targetTypes]);
  const stableElementTypes = useMemo(() => elementTypes, [elementTypes]);
  const routeDismissedScope = buildRouteDismissedScope(
    routeProjectId,
    routeContext,
    dismissedScope
  );

  return useCollaborationHighlightStore((state) => {
    if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return false;
    return state.hasAnyHighlight({
      projectId: routeProjectId,
      parentProjectId,
      context: routeContext,
      targetTypes: stableTargetTypes,
      elementTypes: stableElementTypes,
      dismissedScope: routeDismissedScope,
    });
  });
}

export function useHasBoardStoryboardScopedCollaborationHighlight(params: {
  storyboardId: string | null | undefined;
  targetTypes: readonly CollaborationHighlightTargetType[];
  dismissedScope?: string;
}): boolean {
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();
  const stableTargetTypes = useMemo(() => params.targetTypes, [params.targetTypes]);
  const routeDismissedScope = buildRouteDismissedScope(
    routeProjectId,
    routeContext,
    params.dismissedScope
  );

  return useCollaborationHighlightStore((state) => {
    if (!realtimeCollaborationEnabled || !params.storyboardId || !routeContext || !routeProjectId)
      return false;
    return state.hasStoryboardScopedHighlight({
      projectId: routeProjectId,
      parentProjectId,
      context: routeContext,
      targetStoryboardId: params.storyboardId,
      targetTypes: stableTargetTypes,
      dismissedScope: routeDismissedScope,
    });
  });
}

export function useClearCurrentRouteCollaborationHighlight(): (params: {
  targetType?: CollaborationHighlightTargetType;
  targetId?: string | null;
}) => void {
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return (params) => {
    if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
    useCollaborationHighlightStore.getState().clearHighlight({
      projectId: routeProjectId,
      parentProjectId,
      context: routeContext,
      targetType: params.targetType,
      targetId: params.targetId ?? undefined,
    });
  };
}

export function useDismissCurrentRouteCollaborationHighlightScope(): (scope: string) => void {
  const { routeContext, routeProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return (scope) => {
    if (!realtimeCollaborationEnabled) return;
    const routeDismissedScope = buildRouteDismissedScope(routeProjectId, routeContext, scope);
    if (!routeDismissedScope) return;
    useCollaborationHighlightStore.getState().dismissScope(routeDismissedScope);
  };
}

export function useAcknowledgeResourceLibraryCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  const acknowledgeTarget = useCallback(
    (resourceId: string, category: ResourceLibraryHighlightTab) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeResourceLibraryTargetHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        resourceId,
        category,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );

  const acknowledgeCategoryTab = useCallback(
    (category: ResourceLibraryHighlightTab) => {
      if (!realtimeCollaborationEnabled) return;
      acknowledgeResourceLibraryCategoryTab({
        category,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, realtimeCollaborationEnabled]
  );

  return {
    acknowledgeTarget,
    acknowledgeCategoryTab,
  };
}

export function useAcknowledgeStoryboardCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (storyboardId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeStoryboardTargetHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        storyboardId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export function useAcknowledgeSoundEffectCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (soundEffectId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeSoundEffectTargetHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        soundEffectId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export function useAcknowledgeStoryboardSoundTrackCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (storyboardId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeStoryboardSoundTrackHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        storyboardId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export function useAcknowledgeVoiceoverCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (voiceoverId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeVoiceoverTargetHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        voiceoverId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export function useAcknowledgeStoryboardVoiceTrackCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (storyboardId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeStoryboardVoiceTrackHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        storyboardId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export function useAcknowledgeStoryboardVideoCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (videoId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeStoryboardVideoTargetHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        videoId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export function useAcknowledgeStoryboardVideoTrackCollaborationHighlight() {
  const dismissRouteScope = useDismissCurrentRouteCollaborationHighlightScope();
  const { routeContext, routeProjectId, parentProjectId, realtimeCollaborationEnabled } =
    useRouteCollaborationHighlightContext();

  return useCallback(
    (storyboardId: string) => {
      if (!realtimeCollaborationEnabled || !routeContext || !routeProjectId) return;
      acknowledgeStoryboardVideoTrackHighlight({
        route: {
          projectId: routeProjectId,
          parentProjectId,
          context: routeContext,
        },
        storyboardId,
        dismissRouteScope,
      });
    },
    [dismissRouteScope, parentProjectId, realtimeCollaborationEnabled, routeContext, routeProjectId]
  );
}

export {
  getSoundEffectHighlightScope,
  getStoryboardSoundTrackHighlightScope,
  getVoiceoverHighlightScope,
  getStoryboardVoiceTrackHighlightScope,
  getStoryboardVideoHighlightScope,
  getStoryboardVideoTrackHighlightScope,
};
